import { createAsyncThunk } from '@reduxjs/toolkit';
import { BankQuestionService } from 'Services/axios/BankQuestion.Service';
import { toast } from 'react-toastify';
import Swal from "sweetalert2";
import { handleApiError } from 'Features/Common/ErrorHandler';
import { normalizeDragpinAnswerRows } from 'Component/Learning/BankQuestion/hooks/dragpinAnswer';

/**
 * Append field ke FormData hanya kalau value "berisi".
 * - String: di-trim, kalau hasilnya kosong => tidak dikirim
 * - null/undefined => tidak dikirim
 * - Array kosong => tidak dikirim
 * - Number termasuk 0 => tetap dikirim (0 itu meaningful)
 * - Boolean termasuk false => tetap dikirim (false itu meaningful)
 * - File/Blob => dikirim kalau ada
 */
const appendIfNotEmpty = (formData, key, value) => {
  if (value === null || value === undefined) return;

  // Handle Array: kalau kosong, jangan kirim
  if (Array.isArray(value)) {
    if (value.length === 0) return;
    // untuk array, biarkan caller yang append per index (biar format key konsisten)
    return;
  }

  // Handle File/Blob
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    formData.append(key, value);
    return;
  }

  // Handle String: trim dulu
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    formData.append(key, trimmed);
    return;
  }

  // Handle Number/Boolean/Object lainnya (termasuk 0/false tetap dikirim)
  formData.append(key, value);
};

export const fetchQuestion = createAsyncThunk(
  'Question/fetchQuestion',
  async (filters) => {
    try {
      const response = await BankQuestionService.getAll(filters);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }
);

export const submitQuestion = createAsyncThunk(
  'addQuestion/submitQuestion',
  async (data) => {
    try {
      const formData = new FormData();

      // Kirim hanya yang berisi (kalau kosong, jangan dikirim)
      appendIfNotEmpty(formData, 'title', data.title);
      appendIfNotEmpty(formData, 'description', data.description);
      appendIfNotEmpty(formData, 'type', data.type);
      appendIfNotEmpty(formData, 'answer_explanation', data.answer_explanation);

      // Optional fields: kalau kosong tidak ikut terkirim
      appendIfNotEmpty(formData, 'answer_explanation_video', data.answer_explanation_video);
      appendIfNotEmpty(formData, 'audio_source', data.audio_source);

      // Boolean ini meaningful -> tetap kirim (1/0)
      formData.append('is_interactive', data.is_interactive ? 1 : 0);

      // Field interactive hanya dikirim kalau memang interactive (biar nggak spam kosong ke backend)
      if (data.is_interactive) {
        appendIfNotEmpty(formData, 'interactive_type', data.interactive_type);

        // clickable_type hanya relevan untuk interactive_type=clickable
        if (data.interactive_type === 'clickable') {
          appendIfNotEmpty(formData, 'interactive_detail[0][clickable_type]', data.clickable_type);
        }

        // room_count & pin_count hanya relevan untuk interactive_type=dragpin
        // (hindari ikut terkirim saat mode clickable)
        if (data.interactive_type === 'dragpin') {
          appendIfNotEmpty(formData, 'interactive_detail[0][room_count]', data.room_count);
          appendIfNotEmpty(formData, 'interactive_detail[0][pin_count]', data.pin_count);

          // ✅ Kirim custom room names (array string untuk label custom room)
          const roomCustomNames = data?.interactive_detail?.room_custom_names;
          if (Array.isArray(roomCustomNames)) {
            roomCustomNames.forEach((roomName, idx) => {
              if (roomName && typeof roomName === 'string' && roomName.trim() !== '') {
                appendIfNotEmpty(formData, `interactive_detail[0][room_custom_names][${idx}]`, roomName.trim());
              }
            });
          }

          // ✅ Kirim custom pin names (array string untuk label custom pin)
          const pinCustomNames = data?.interactive_detail?.pin_custom_names;
          if (Array.isArray(pinCustomNames)) {
            pinCustomNames.forEach((pinName, idx) => {
              if (pinName && typeof pinName === 'string' && pinName.trim() !== '') {
                appendIfNotEmpty(formData, `interactive_detail[0][pin_custom_names][${idx}]`, pinName.trim());
              }
            });
          }

          // ✅ Kirim meta label pins (wajib diisi, digunakan untuk table)
          const dragpinPinsMeta = data?.interactive_detail?.dragpin_pins;
          if (Array.isArray(dragpinPinsMeta)) {
            dragpinPinsMeta.forEach((pin, idx) => {
              if (!pin) return;
              // NOTE: simpan name agar backend punya mapping stabil
              appendIfNotEmpty(formData, `interactive_detail[0][dragpin_pins][${idx}][name]`, pin.name);
              appendIfNotEmpty(formData, `interactive_detail[0][dragpin_pins][${idx}][label]`, pin.label);
            });
          }
        }

        // step hanya relevan untuk bar_chart / line_chart
        if (data.interactive_type === 'bar_chart' || data.interactive_type === 'line_chart') {
          appendIfNotEmpty(formData, 'interactive_detail[0][step]', data.step);
        }

        // ✅ first_node & second_node hanya relevan untuk sequential (ambil dari answer kunci)
        if (data.interactive_type === 'sequential') {
          const firstNode = data.answer?.[0] ?? '';
          const secondNode = data.answer?.[1] ?? '';
          appendIfNotEmpty(formData, 'interactive_detail[0][first_node]', firstNode);
          appendIfNotEmpty(formData, 'interactive_detail[0][second_node]', secondNode);
        }
      }

      // Handle choice & answer as array
      if (data.type === 'fill_in_the_blanks') {
        const answersToSubmit = data.interactive_type === 'dragpin'
          ? normalizeDragpinAnswerRows(data.answer)
          : (data.answer || []);

        // Skip jawaban kosong (tidak dikirim)
        answersToSubmit.forEach((answers, index) => {
          if (typeof answers === 'string') {
            const trimmed = answers.trim();
            if (trimmed !== '') formData.append(`answer[${index}]`, trimmed);
          } else if (answers !== null && answers !== undefined) {
            formData.append(`answer[${index}]`, answers);
          }
        });
      } else if (data.type === 'sentence_arrangement') {
        // Skip jawaban kosong (tidak dikirim)
        (data.answer || []).forEach((answers, index) => {
          if (typeof answers === 'string') {
            const trimmed = answers.trim();
            if (trimmed !== '') formData.append(`answer[${index}]`, trimmed);
          } else if (answers !== null && answers !== undefined) {
            formData.append(`answer[${index}]`, answers);
          }
        });

        // Skip choice kosong (sudah sesuai request)
        (data.choice || []).forEach((choices, index) => {
          if (typeof choices === 'string' && choices.trim() !== '') {
            formData.append(`choice[${index}]`, choices.trim());
          }
        });
      } else if (data.type === 'multiple_choice') {
        // multiple_choice: "0" itu valid (unselected), jadi jangan di-skip
        (data.answer || []).forEach((answers, index) => {
          if (answers !== null && answers !== undefined && String(answers).trim() !== '') {
            formData.append(`answer[${index}]`, answers);
          }
        });

        // choice kosong tetap tidak dikirim
        (data.choice || []).forEach((choices, index) => {
          if (typeof choices === 'string' && choices.trim() !== '') {
            formData.append(`choice[${index}]`, choices.trim());
          }
        });
      }

      let response;
      if (data.isUpdate) {
        response = await BankQuestionService.update(data.id, formData);
      } else {
        response = await BankQuestionService.create(formData);
      }

      toast.success(response.data.message, {
        position: toast.POSITION.TOP_RIGHT
      });
      data.successCallback && data.successCallback();
      return response.data.data;
    } catch (error) {
      const fieldErrors = error?.response?.data?.errors;
      let showedFieldError = false;

      if (fieldErrors && typeof fieldErrors === 'object') {
        for (const errorField in fieldErrors) {
          if (!Object.prototype.hasOwnProperty.call(fieldErrors, errorField)) continue;
          const errorMessages = fieldErrors[errorField];
          let errorMessage = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages;
          if (errorField === "answer_explanation") {
            errorMessage = "Penjelasan kunci jawaban harus diisi dengan benar.";
          } else if (errorField === "choice") {
            errorMessage = "Pilihan jawaban harus diisi dengan benar.";
          } else if (errorField === "answer" && data.type === 'multiple_choice') {
            errorMessage = "Status pilihan jawaban harus diisi dengan benar.";
          }
          if (errorMessage) {
            showedFieldError = true;
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT
            });
          }
        }
      }

      if (error.code === "ERR_NETWORK") {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Permintaan gagal. Mohon periksa koneksi internet Anda.',
        });
      } else if (error?.response?.status === 500) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Terjadi kesalahan sistem. Mohon hubungi pengembang.',
        });
      } else if (!showedFieldError && error?.response?.status === 422) {
        Swal.fire({
          icon: 'warning',
          title: 'Soal terkunci',
          text:
            error?.response?.data?.message ||
            'Soal ini sudah dikerjakan siswa dan tidak dapat diubah.',
        });
      }
    }
  }
);

export const deleteQuestion = createAsyncThunk(
  'deleteQuestion/deleteQuestion',
  async (id) => {
    try {
      const response = await BankQuestionService.delete(id);
      toast.success(response.data.message, {
        position: toast.POSITION.TOP_RIGHT
      });
      return response.data.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }
);

export const importQuestion = createAsyncThunk(
  'importQuestion/importQuestion',
  async (data) => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('countChoices', data.countChoices);
      const response = await BankQuestionService.import(formData);
      toast.success(`Import ${response.data["total data success"]} soal berhasil.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      data.successCallback && data.successCallback();
      return response.data.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }
);